import React, { useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';

const ProcessStepDetail = () => {
  const { stepKey } = useParams();
  const navigate = useNavigate();

  // Ensuring the user starts at the top of the detail page
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [stepKey]);

  const processSteps = {
    "co-create-and-enable": {
      title: "Co-create and enable",
      icon: "/images/strategy.png",
      tagline: "Collaborative Innovation & Team Empowerment",
      description: "We partner with your organization to co-create AI strategies and enable your team with the skills and tools needed for long-term success. This collaborative approach ensures organizational buy-in, knowledge transfer, and sustainable AI adoption.",
      keyBenefits: ["Joint strategy development", "Team upskilling", "Knowledge transfer", "Cultural transformation", "Reduced resource dependency"],
      deliverables: ["Strategy Framework", "Custom Training", "Implementation Playbooks", "Change Management Plan"],
      process: ["Stakeholder workshops", "Skills assessment", "Curriculum development", "Hands-on training sessions", "Adoption support"]
    },
    "innovation": {
      title: "Innovation",
      icon: "/images/ecosystem.png",
      tagline: "Continuous Innovation & Future-Ready Solutions",
      description: "Foster a culture of continuous innovation through advanced AI technologies, experimentation frameworks, and emerging technology adoption. We help you stay ahead of the curve.",
      keyBenefits: ["Cutting-edge AI access", "Rapid prototyping", "Innovation pipeline", "Competitive advantage", "R&D collaboration"],
      deliverables: ["Innovation Framework", "Technology Roadmap", "Proof of Concepts", "Pilot Implementations"],
      process: ["Landscape analysis", "Opportunity identification", "Prototyping", "Testing & iteration", "Scale-up strategy"]
    },
    "data-driven-strategy": {
      title: "Data driven strategy",
      icon: "/images/packaging.png",
      tagline: "Strategic Intelligence Through Data",
      description: "Develop comprehensive data-driven strategies that align AI initiatives with business objectives. We transform raw data into strategic assets that drive decision-making.",
      keyBenefits: ["Data-informed decisions", "Strategic alignment", "Actionable insights", "Operational efficiency", "Measurable ROI"],
      deliverables: ["Data Strategy Document", "Analytics Framework", "BI Dashboards", "Governance Policies"],
      process: ["Landscape assessment", "Asset identification", "Framework development", "System implementation", "Insight generation"]
    },
    "tailored-ai-solution": {
      title: "Tailored AI solution",
      icon: "/images/innovation.png",
      tagline: "Custom AI Built for Your Unique Needs",
      description: "Design and develop custom AI solutions specifically crafted for your unique business challenges. From machine learning models to intelligent automation.",
      keyBenefits: ["Custom-built models", "Enterprise scalability", "Seamless integration", "Production-ready", "Performance tuning"],
      deliverables: ["Custom Algorithms", "API Layer", "Deployment Package", "Technical Docs"],
      process: ["Requirements gathering", "Architecture design", "Model development", "Validation", "Production rollout"]
    },
    "data-privacy": {
      title: "Data privacy",
      icon: "/images/group.png",
      tagline: "Security, Privacy & Compliance First",
      description: "Implement robust data privacy and security measures to protect sensitive information while enabling AI innovation. We ensure global compliance standards.",
      keyBenefits: ["Regulatory compliance", "Data encryption", "Privacy-preserving AI", "Audit accountability", "Risk mitigation"],
      deliverables: ["Privacy Impact Assessment", "Protection Framework", "Security Plan", "Compliance Docs"],
      process: ["Requirements analysis", "Risk assessment", "Security architecture", "Verification", "Ongoing audit"]
    },
    "ethical-ai": {
      title: "Ethical AI",
      icon: "/images/strategy.png",
      tagline: "Responsible & Fair AI Development",
      description: "Build AI systems with ethics and fairness at the core. We implement responsible AI practices that ensure transparency, accountability, and bias mitigation.",
      keyBenefits: ["Bias detection", "Explainable models", "Ethical framework", "Social responsibility", "Stakeholder trust"],
      deliverables: ["Ethical Framework", "Bias Assessment", "Fairness Dashboard", "Governance Guidelines"],
      process: ["Principles alignment", "Fairness assessment", "Explainability integration", "Testing", "Ethical monitoring"]
    }
  };

  const currentStep = processSteps[stepKey];

  if (!currentStep) {
    return (
      <div className="pt-24 min-h-screen flex flex-col items-center justify-center bg-white">
        <h1 className="text-4xl font-black text-[#17091a] mb-6 tracking-tighter">Process Not Found</h1>
        <Link to="/" className="text-[#952877] font-bold hover:underline uppercase text-[10px] tracking-[0.3em]">Return to Home</Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white selection:bg-[#952877]/10 selection:text-[#952877]">
      
      {/* --- HERO HEADER --- */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        {/* Background Ambient Glows */}
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-[#952877]/5 blur-[120px] rounded-full pointer-events-none"></div>
        <div className="absolute bottom-0 left-0 w-[300px] h-[300px] bg-[#592aa7]/5 blur-[100px] rounded-full pointer-events-none"></div>

        <div className="max-w-5xl mx-auto px-6 relative z-10">
          <button 
            onClick={() => navigate(-1)}
            className="group flex items-center text-[#17091a]/60 hover:text-[#952877] mb-12 transition-all font-black text-[10px] uppercase tracking-[0.3em] outline-none"
          >
            <svg className="w-4 h-4 mr-2 transition-transform group-hover:-translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M15 19l-7-7 7-7" />
            </svg>
            Go Back
          </button>

          <div className="flex flex-col md:flex-row md:items-center gap-10 mb-12">
            <div className="w-24 h-24 bg-white rounded-3xl p-4 shadow-xl shadow-[#952877]/5 border border-gray-100 flex items-center justify-center">
              <img src={currentStep.icon} alt={currentStep.title} className="w-full h-full object-contain" />
            </div>
            <div>
              <div className="inline-block px-3 py-1 mb-4 rounded-full border border-[#952877]/20 bg-[#952877]/5">
                <span className="text-[10px] font-black tracking-[0.2em] uppercase text-[#952877]">Strategic Step</span>
              </div>
              <h1 className="text-4xl md:text-6xl font-black text-[#17091a] tracking-tighter leading-none mb-4">
                {currentStep.title}
              </h1>
              <p className="text-xl text-[#952877] font-light italic">{currentStep.tagline}</p>
            </div>
          </div>

          <p className="text-xl text-gray-500 font-light leading-relaxed max-w-3xl">
            {currentStep.description}
          </p>
        </div>
      </section>

      {/* --- CONTENT ARCHITECTURE --- */}
      <section className="pb-32 relative z-10">
        <div className="max-w-5xl mx-auto px-6 grid gap-12">
          
          <div className="grid md:grid-cols-2 gap-12">
            {/* Key Benefits Card */}
            <div className="bg-[#fafafa] border border-gray-100 rounded-[3rem] p-10 shadow-sm transition-all hover:shadow-xl hover:-translate-y-1">
              <h2 className="text-[10px] font-black uppercase tracking-[0.4em] text-[#952877] mb-10">Key Advantages</h2>
              <ul className="space-y-6">
                {currentStep.keyBenefits.map((benefit, index) => (
                  <li key={index} className="flex items-center gap-4 group">
                    <div className="w-6 h-6 rounded-full bg-[#952877]/10 flex items-center justify-center flex-shrink-0 transition-all group-hover:bg-[#952877]">
                      <svg className="w-3 h-3 text-[#952877] group-hover:text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={4} d="M5 13l4 4L19 7" /></svg>
                    </div>
                    <span className="text-sm font-bold text-[#17091a]/80 uppercase tracking-tight">{benefit}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Workflow Timeline Card */}
            <div className="bg-white/40 backdrop-blur-xl border border-white/60 rounded-[3rem] p-10 shadow-[0_32px_64px_-16px_rgba(0,0,0,0.05)]">
              <h2 className="text-[10px] font-black uppercase tracking-[0.4em] text-[#17091a]/40 mb-10">The Methodology</h2>
              <div className="space-y-10">
                {currentStep.process.map((step, index) => (
                  <div key={index} className="flex gap-6 group">
                    <div className="text-[10px] font-black text-[#952877] py-1 border-r border-[#952877]/20 pr-4 min-w-[40px]">
                      0{index + 1}
                    </div>
                    <p className="text-sm font-medium text-gray-600 leading-relaxed group-hover:text-[#17091a] transition-colors">
                      {step}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Deliverables Grid */}
          <div className="bg-white/40 backdrop-blur-xl border border-white/60 rounded-[3rem] p-10 md:p-16 shadow-[0_32px_64px_-16px_rgba(0,0,0,0.05)] relative overflow-hidden">
            <div className="absolute top-0 right-0 w-64 h-64 bg-[#952877]/5 blur-[80px] rounded-full pointer-events-none"></div>
            <h2 className="text-[10px] font-black uppercase tracking-[0.4em] text-[#17091a]/40 mb-12 text-center">Core Deliverables</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
              {currentStep.deliverables.map((deliverable, index) => (
                <div key={index} className="flex flex-col items-center text-center group">
                  <div className="w-14 h-14 rounded-2xl bg-white shadow-sm flex items-center justify-center mb-5 transition-all group-hover:rotate-6 group-hover:scale-110 group-hover:shadow-lg">
                    <svg className="w-6 h-6 text-[#952877]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  </div>
                  <span className="text-[10px] font-black text-[#17091a] uppercase tracking-wider leading-tight">
                    {deliverable}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Contact / CTA */}
          <div className="relative group overflow-hidden rounded-[4rem] bg-[#17091a] p-12 md:p-24 text-center">
             <div className="absolute -top-24 -right-24 w-96 h-96 bg-[#952877]/20 blur-[100px] rounded-full"></div>
             <div className="relative z-10">
                <h2 className="text-4xl md:text-6xl font-black text-white mb-10 tracking-tighter leading-tight">Ready to initiate <br/> this strategy?</h2>
                <Link 
                  to="/contact"
                  className="inline-flex items-center px-12 py-6 bg-white text-[#17091a] font-black text-[10px] uppercase tracking-[0.3em] rounded-2xl hover:bg-[#952877] hover:text-white transition-all duration-500 shadow-xl hover:-translate-y-2"
                >
                  Consult Our Engineers
                  <svg className="w-4 h-4 ml-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M17 8l4 4m0 0l-4 4m4-4H3" /></svg>
                </Link>
             </div>
          </div>

        </div>
      </section>
    </div>
  );
};

export default ProcessStepDetail;